import React, { FormEvent, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRightIcon, CheckCircle2Icon, Loader2Icon, MapPinIcon } from "lucide-react";
import { company, zones } from "../../data/company";
type Status = "idle" | "submitting" | "success";
type Errors = Partial<Record<"name" | "phone" | "type", string>>;
const workTypes = ["Peinture intérieure", "Peinture extérieure", "Enduit de façade", "Vitrerie", "Rénovation intérieure", "Autre"];
export function QuoteSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<Errors>({});
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const next: Errors = {};
    if (!String(data.get("name") || "").trim()) next.name = "Indiquez votre nom.";
    const phone = String(data.get("phone") || "").replace(/\s/g, "");
    if (!/^(\+33|0)[1-9]\d{8}$/.test(phone)) next.phone = "Numéro de téléphone invalide.";
    if (!data.get("type")) next.type = "Choisissez un type de travaux.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus("submitting");
    window.setTimeout(() => setStatus("success"), 900);
  };
  const fieldClass = (hasError: boolean) => `mt-2 w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-ink placeholder:text-muted/70 focus:outline-none focus:ring-2 focus:ring-accent ${hasError ? "border-red-500" : "border-line"}`;
  return <section id="devis" aria-labelledby="quote-title" className="scroll-mt-24 px-3 pb-3 lg:px-5 lg:pb-5" data-id="element-125" data-mp="37ui0af3zqsov">
      <div className="mx-auto grid max-w-[1440px] gap-4 lg:grid-cols-[1fr_1.1fr] lg:gap-5" data-id="element-126" data-mp="3mhl0wunm1hgf">
        <div className="flex flex-col justify-between rounded-3xl bg-accent px-6 py-12 text-white sm:px-10 lg:px-14 lg:py-16" data-id="element-127" data-mp="3hkktogf0y8nq">
          <div data-id="element-128" data-mp="3vjuclnum7gve">
            <h2 id="quote-title" className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-129" data-mp="3vh851o7q51d6">
              Parlons de votre projet
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-white/80" data-id="element-130" data-mp="2gktkq9zmy7r">
              Décrivez vos travaux en quelques lignes. Je vous rappelle pour convenir d'une visite et établir un devis gratuit.
            </p>
          </div>

          <div className="mt-14" data-id="element-131" data-mp="cbush553aphb">
            <h3 className="flex items-center gap-2 text-sm font-bold" data-id="element-132" data-mp="h4xr3roq4p4d">
              <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-133" data-mp="h68pfbzeh4o3" />
              Zone d'intervention — Meaux et alentours
            </h3>
            <ul className="mt-4 flex flex-wrap gap-2" data-id="element-134" data-mp="q4ax2adn2vkc">
              {zones.map(z => <li key={z} className="rounded-full bg-white/15 px-3.5 py-1.5 text-sm font-medium text-white" data-id="element-135" data-mp="v3xd0rkuqpwu">
                  {z}
                </li>)}
            </ul>
            <p className="mt-8 text-sm text-white/75" data-id="element-136" data-mp="2wvtqi6qky9u9">
              {company.address}, {company.city}
            </p>
          </div>
        </div>

        <div className="rounded-3xl bg-surface px-6 py-12 sm:px-10 lg:px-14 lg:py-16" data-id="element-137" data-mp="31v6it7080948">
          <AnimatePresence mode="wait" initial={false} data-id="element-138" data-mp="1j0btt8hw2wlq">
            {status === "success" ? <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="flex h-full flex-col items-start justify-center" role="status" data-id="element-139" data-mp="1e80k5dxcjwfz">
                <CheckCircle2Icon className="h-12 w-12 text-accent" aria-hidden="true" data-id="element-140" data-mp="1xy485xbp9eyc" />
                <h3 className="mt-6 text-3xl font-bold tracking-tight text-ink" data-id="element-141" data-mp="1swn8g54ybyjc">Demande envoyée</h3>
                <p className="mt-3 max-w-sm leading-relaxed text-muted" data-id="element-142" data-mp="1nxk30jl27qau">
                  Merci ! Je vous rappelle rapidement pour convenir d'un rendez-vous.
                </p>
                <button type="button" onClick={() => setStatus("idle")} className="mt-8 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-ink-soft" data-id="element-143" data-mp="cix3yx9x7896">
                  Envoyer une autre demande
                </button>
              </motion.div> : <motion.form key="form" noValidate onSubmit={handleSubmit} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.15
          }} className="grid gap-5 sm:grid-cols-2" data-id="element-144" data-mp="1wekijt82ihgo">
                <div data-id="element-145" data-mp="2a5pk50uzxjtn">
                  <label htmlFor="name" className="text-sm font-semibold text-ink" data-id="element-146" data-mp="2a8bpctuezn8p">Nom</label>
                  <input id="name" name="name" autoComplete="name" placeholder="Jean Dupont" className={fieldClass(!!errors.name)} aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} data-id="element-147" data-mp="2f752gl5shc8c" />
                  {errors.name && <p id="name-error" className="mt-1.5 text-sm text-red-600" data-id="element-148" data-mp="w5ur9w9kik9n">{errors.name}</p>}
                </div>
                <div data-id="element-149" data-mp="r6852xbrhtop">
                  <label htmlFor="phone" className="text-sm font-semibold text-ink" data-id="element-150" data-mp="r6rl28huq803">Téléphone</label>
                  <input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="06 12 34 56 78" className={fieldClass(!!errors.phone)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "phone-error" : undefined} data-id="element-151" data-mp="156iyo84ghmdk" />
                  {errors.phone && <p id="phone-error" className="mt-1.5 text-sm text-red-600" data-id="element-152" data-mp="8hbizgqiq7hk">{errors.phone}</p>}
                </div>
                <div data-id="element-153" data-mp="m987zbww419q">
                  <label htmlFor="city" className="text-sm font-semibold text-ink" data-id="element-154" data-mp="m9rja9s1bres">Commune</label>
                  <input id="city" name="city" autoComplete="address-level2" placeholder="Meaux" className={fieldClass(false)} data-id="element-155" data-mp="hfwwuyl1oyc0" />
                </div>
                <div data-id="element-156" data-mp="3kygllr4ilm22">
                  <label htmlFor="type" className="text-sm font-semibold text-ink" data-id="element-157" data-mp="3kx5gnnzc289d">Type de travaux</label>
                  <select id="type" name="type" defaultValue="" className={fieldClass(!!errors.type)} aria-invalid={!!errors.type} aria-describedby={errors.type ? "type-error" : undefined} data-id="element-158" data-mp="1ou92w2g671bl">
                    <option value="" disabled data-id="element-159" data-mp="1ylwnbjeb7i3k">Sélectionner</option>
                    {workTypes.map(w => <option key={w} value={w} data-id="element-160" data-mp="23in5cc9ocnrm">{w}</option>)}
                  </select>
                  {errors.type && <p id="type-error" className="mt-1.5 text-sm text-red-600" data-id="element-161" data-mp="15yam2hsk0pjv">{errors.type}</p>}
                </div>
                <div className="sm:col-span-2" data-id="element-162" data-mp="1ax3xm4oa66he">
                  <label htmlFor="message" className="text-sm font-semibold text-ink" data-id="element-163" data-mp="1ftaznmdk2xu2">Votre projet</label>
                  <textarea id="message" name="message" rows={5} placeholder="Ex. : repeindre un séjour de 25 m² et deux chambres, plafonds compris." className={`${fieldClass(false)} resize-none`} data-id="element-164" data-mp="1jwqq20z181yb" />
                </div>
                <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-165" data-mp="2v9b4xjvp9l36">
                  <p className="text-xs leading-relaxed text-muted" data-id="element-166" data-mp="jplongbwizit">Vos informations servent uniquement à vous recontacter.</p>
                  <button type="submit" disabled={status === "submitting"} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-ink px-7 py-3.5 text-[15px] font-semibold text-white transition-colors duration-150 hover:bg-ink-soft disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" data-id="element-167" data-mp="auxilnyi5dsy">
                    {status === "submitting" ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-168" data-mp="3j4gzl8g5bvf6" />
                        Envoi…
                      </> : <>
                        Envoyer ma demande
                        <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-169" data-mp="q66vsuidf9qb" />
                      </>}
                  </button>
                </div>
              </motion.form>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}